import { useParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Mail, Clock, Upload, X, Loader2, Paperclip, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { publicTicketApi, type PublicReplyAttachment } from '../api/tickets';
import { StatusBadge } from '../components/badges';
import EmptyState from '../components/EmptyState';

function fmtDateTime(d?: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at '
    + dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_REPLY_FILES = 10;

export default function PublicTicketView() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-ticket', token],
    queryFn: () => publicTicketApi.view(token!),
    enabled: !!token,
    retry: false,
  });

  const [replyText, setReplyText] = useState('');
  const [attachments, setAttachments] = useState<PublicReplyAttachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const replyMutation = useMutation({
    mutationFn: () => publicTicketApi.reply(token!, { content: replyText.trim(), attachments }),
    onSuccess: () => {
      setReplyText('');
      setAttachments([]);
      queryClient.invalidateQueries({ queryKey: ['public-ticket', token] });
      toast.success('Your reply was added');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to send reply'),
  });

  const uploadFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (attachments.length + arr.length > MAX_REPLY_FILES) {
      toast.error(`Maximum ${MAX_REPLY_FILES} files`);
      return;
    }
    setUploading((n) => n + arr.length);
    const results = await Promise.allSettled(arr.map((f) => publicTicketApi.uploadAttachment(token!, f)));
    const ok: PublicReplyAttachment[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok.push(r.value);
      else toast.error(`Failed to upload ${arr[i].name}`);
    });
    if (ok.length) setAttachments((prev) => [...prev, ...ok]);
    setUploading((n) => Math.max(0, n - arr.length));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '100vh', background: 'var(--scpa-bg)' }}>
        <p className="text-muted">Loading ticket…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center px-4" style={{ minHeight: '100vh', background: 'var(--scpa-bg)' }}>
        <div className="card max-w-md text-center" style={{ padding: '2rem' }}>
          <AlertTriangle className="w-12 h-12 text-warn mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-fg mb-2">Link expired or invalid</h1>
          <p className="text-sm text-muted leading-relaxed">
            This ticket link is no longer valid. It may have expired (links work for 30 days) or it
            may have been altered. Reply to the most recent support email to continue, or contact
            support directly.
          </p>
        </div>
      </div>
    );
  }

  const { ticket, viewerEmail } = data;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--scpa-bg)' }}>
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 flex flex-col gap-6">
        {/* Header */}
        <div>
          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1">
            {ticket.company?.name || 'Support'}
          </p>
          <div className="flex items-start gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-fg flex-1">{ticket.title}</h1>
            <StatusBadge status={ticket.status} />
          </div>
          <p className="text-sm text-muted mt-1 font-mono font-bold text-primary">{ticket.ticketNumber}</p>
        </div>

        {/* Viewer badge */}
        <div className="flex items-center gap-2 text-xs text-muted card" style={{ padding: '0.5rem 0.75rem' }}>
          <Mail className="w-3.5 h-3.5" />
          <span>Viewing as <span className="font-medium text-fg">{viewerEmail}</span></span>
        </div>

        {/* Ticket meta */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="px-5 py-4 border-b border-line grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted">Opened</p>
              <p className="text-fg font-medium">{fmtDateTime(ticket.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Assigned to</p>
              <p className="text-fg font-medium">{ticket.assignedTo?.name || 'Unassigned'}</p>
            </div>
            {ticket.resolvedAt && (
              <div>
                <p className="text-xs text-muted">Resolved</p>
                <p className="text-fg font-medium">{fmtDateTime(ticket.resolvedAt)}</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="px-5 py-4">
            <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-2">Description</p>
            <p className="text-sm text-fg whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
          </div>
        </div>

        {/* Conversation */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg">Conversation</h2>
            <span className="text-xs text-muted">{ticket.messages.length} message{ticket.messages.length !== 1 ? 's' : ''}</span>
          </div>

          {ticket.messages.length === 0 ? (
            <EmptyState>No replies yet.</EmptyState>
          ) : (
            <ul>
              {ticket.messages.map((msg: any) => (
                <li key={msg.id} className="px-5 py-4 border-b border-line-light last:border-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-semibold text-fg">{msg.authorName || msg.author?.name || 'Support'}</span>
                    <span className="text-xs text-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtDateTime(msg.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-fg whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {msg.attachments.map((att: any) => (
                        <li key={att.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-row-odd border border-line-light text-xs text-muted">
                          <Paperclip className="w-3 h-3 text-muted" />
                          <span className="truncate max-w-[12rem]">{att.filename}</span>
                          <span className="text-muted">{fmtSize(att.size)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Reply composer */}
        {((ticket as any).statusCategory ?? ticket.status) !== 'closed' ? (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="px-5 py-3.5 border-b border-line">
              <h2 className="text-sm font-semibold text-fg">Add a reply</h2>
            </div>
            <div className="p-5 space-y-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={4}
                placeholder="Add more detail, a new thought, or a screenshot…"
                className="input resize-y"
              />

              {/* Drop zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-3 transition-colors cursor-pointer ${
                  dragOver ? 'border-primary bg-primary-muted' : 'border-line hover:border-primary hover:bg-row-odd'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt,.md,.log,.json,.zip,.csv,.xls,.xlsx,.ppt,.pptx"
                  onChange={(e) => e.target.files && uploadFiles(e.target.files)}
                />
                {uploading > 0 ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" /> Uploading {uploading} file{uploading !== 1 ? 's' : ''}…
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Upload className="w-4 h-4 text-muted" />
                    <span><span className="text-primary font-medium">Attach files</span> or drag &amp; drop (images, PDFs)</span>
                  </div>
                )}
              </div>

              {attachments.length > 0 && (
                <ul className="space-y-1">
                  {attachments.map((att) => (
                    <li key={att.path} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-row-odd border border-line-light">
                      <Paperclip className="w-4 h-4 text-muted shrink-0" />
                      <span className="flex-1 text-sm truncate text-fg">{att.filename}</span>
                      <span className="text-xs text-muted shrink-0">{fmtSize(att.size)}</span>
                      <button
                        type="button"
                        onClick={() => setAttachments((prev) => prev.filter((a) => a.path !== att.path))}
                        className="shrink-0 text-muted hover:text-err"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={replyMutation.isPending || uploading > 0 || (!replyText.trim() && attachments.length === 0)}
                  onClick={() => replyMutation.mutate()}
                  className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {replyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send reply
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted text-center">
            This ticket is closed. Reply to the email you received to reopen it.
          </p>
        )}

        {/* Footer */}
        <p className="text-xs text-muted text-center">
          You can also reply to the email you received. Internal notes are not shown.
        </p>
      </div>
    </div>
  );
}
